import type { ProductModelV2, ProductSearchReqModel } from '@develop/ia-api-library'
import { searchProductsV2 } from '@develop/ia-api-library'
import { convertDateTimeToTimestamp } from '@develop/ia-utils'
import { defineStore } from 'pinia'
import type { CheckoutCartModel, CheckoutEPrescriptionModel, CheckoutPrescriptionModel, CheckoutUnavailableProductModel } from '@/models/checkout-cart'
import { PrescriptionVariant } from '@/models/prescription'
import type { PrescriptionUniversal } from '@/models/prescription-universal'
import type { AddProductResult } from '@/models/product'
import { resetLegacyVuex, useLegacyVuex } from '~/composables/legacy-vuex'

export const useCartStore = defineStore('cart', () => {
  const checkoutStore = useCheckoutStore()
  const couponStore = useCouponStore()
  const currentPharmacyStore = useCurrentPharmacyStore()
  const anonymousUser = useAnonymousUser()

  const DEFAULT_CART: CheckoutCartModel = {
    ePrescriptions: [],
    prescriptions: [],
    products: [],
    unavailableProducts: [],
    myLife: true,
  }

  // Flag that determines if data from local storage is loaded, to prevent showing wrong data while loading
  const isStoreInitialized = ref(false)
  const cartData = ref<CheckoutCartModel>(DEFAULT_CART)

  // TODO: Prescriptions should have ids stored in database
  const prescriptions = computed(() => cartData.value.prescriptions || [])
  const ePrescriptions = computed(() => cartData.value.ePrescriptions || [])
  const allPrescriptions = computed(() => {
    const combined = [...prescriptions.value ?? [], ...ePrescriptions.value ?? []] as PrescriptionUniversal[]
    return combined.sort((a, b) => convertDateTimeToTimestamp(b.createdAt) - convertDateTimeToTimestamp(a.createdAt))
  })
  const removePrescriptions = () => {
    cartData.value.prescriptions = []
    cartData.value.ePrescriptions = []
  }

  const products = computed(() => cartData.value.products)
  const productsWithDetails = ref<ProductModelV2[]>([])
  const removeProducts = () => {
    cartData.value.products = []
  }

  const unavailableProducts = computed(() => cartData.value.unavailableProducts)
  const addUnavailableProduct = (product: CheckoutUnavailableProductModel) => {
    const existingProduct = unavailableProducts.value.find(p => p.pzn === product.pzn)

    if (!existingProduct)
      cartData.value.unavailableProducts.push(product)
  }
  const removeUnavailableProducts = () => {
    cartData.value.unavailableProducts = []
  }
  const generalTracking = isClient ? useGeneralTracking() : null

  const hasNonPayableProducts = computed(() => productsWithDetails.value.some(p => !p.isPayable))
  const hasNonPayableProductsOnly = computed(() => productsWithDetails.value.every(p => !p.isPayable))

  const cartQuantity = computed(() => allPrescriptions.value.length + products.value.length)
  const isCartEmpty = computed(() => cartQuantity.value === 0)
  const isFetchingFirstProduct = ref(false)

  const hasPrescriptions = computed(() => allPrescriptions.value.length > 0)
  const hasPrescriptionsOnly = computed(() => allPrescriptions.value.length > 0 && products.value.length === 0)

  const hasExpressProducts = computed(() => productsWithDetails.value.some(p => p.delivery?.express.value))
  const hasOnlyExpressProducts = computed(() => productsWithDetails.value.length > 0 && productsWithDetails.value.every(p => p.delivery?.express.value))
  const hasNonExpressProducts = computed(() => productsWithDetails.value.some(p => !p.delivery?.express.value))

  const isCartMixed = computed(() => hasExpressProducts.value && hasNonExpressProducts.value)

  const isLoading = ref(false)
  const setIsLoading = (value: boolean) => isLoading.value = value

  const coupons = ref<{ couponCode: string; valid: boolean }[]>([])

  const validCoupons = computed(() => coupons.value.filter(coupon => coupon.valid))

  const couponAlert = ref<{
    message: string
    type: 'success' | 'error'
  }>({
    message: '',
    type: 'success',
  })
  // Has checked coupon code, may or may not be valid
  const hasCoupon = ref(false)

  const setCouponAlertMessage = (alertData: { message: string; type: 'success' | 'error' }) => {
    set(couponAlert, alertData)
  }

  const removeCoupon = async () => {
    couponStore.removeCoupon()
    set(hasCoupon, false)
    coupons.value = []
    setCouponAlertMessage({ message: '', type: 'success' })

    await checkoutStore.updateDraft()
    await nextTick()
  }

  const fetchProductsWithDetails = async () => {
  // This check if for adding prescriptions
  // In that case there are no products so the endpoint returns error 500
    if (!products.value.length) {
      set(productsWithDetails, [])
      return
    }

    const payload: ProductSearchReqModel = {
      searchTerm: 'match_all',
      sorting: 'name-asc',
      pharmacyId: currentPharmacyStore.pharmacyId!,
      resultsPerPage: products.value.length,
      activeSubstances: [],
      categories: [],
      dosages: [],
      producers: [],
      products: products.value.map(p => p.pzn.toString()),
      type: 'pharmacy',
      volumes: [],
    }

    const response = await searchProductsV2(1, payload)

    if (!response.success)
      return

    if (response.data?.results)
      set(productsWithDetails, response.data.results)
  }

  const legacyVuex = useLegacyVuex()

  watchDeep(cartData, (newValue) => {
    const { ePrescriptions, prescriptions, products } = newValue

    if (!ePrescriptions.length && !prescriptions.length && !products.length)
      return

    const cartEntries = Object.entries(newValue)
    const legacyCartDto = cartEntries.reduce<Record<string, any>>((acc, [key, value]) => {
      if (key !== 'myLife')
        acc[key] = value
      return acc
    }, {}) as CheckoutCartModel

    set(legacyVuex, { ...legacyVuex.value, cart: legacyCartDto, owner: anonymousUser.getToken() })
  })

  watch(() => ({ cart: cartData, pharmacyId: currentPharmacyStore.pharmacyId }), async () => {
    if (isCartEmpty.value || !currentPharmacyStore.pharmacyId)
      return

    setIsLoading(true)

    await checkoutStore.updateDraft()
    await fetchProductsWithDetails()
    setIsLoading(false)
    isFetchingFirstProduct.value = false
  }, { deep: true })

  const getProductInCart = (product: ProductModelV2) => {
    return cartData.value?.products.find(p => p.pzn === product.pzn)
  }

  const getProductInCartAmount = (product: ProductModelV2) => {
    return getProductInCart(product)?.amount ?? 0
  }

  /**
 * Use `useAddToCart` to handle results instead of using this directly
 */
  const addProduct = (product: ProductModelV2, amountToAdd = 1): AddProductResult => {
    const { price } = useProductPrice(product)
    const productInCart = getProductInCart(product)
    const productInCartAmount = getProductInCartAmount(product)

    const maxQuantity = product.maxQuantity || 0

    const validQuantity = () => (productInCartAmount + amountToAdd) <= maxQuantity

    if (!validQuantity())
      return { code: 'invalid-quantity' }

    if (productInCart) {
      productInCart.amount += amountToAdd
    } else {
      if (cartQuantity.value === 0)
        isFetchingFirstProduct.value = true

      cartData.value?.products.push({
        pzn: product.pzn,
        price: price.value!,
        amount: amountToAdd,
      })
    }

    const couponCode = product.coupon?.couponCode.trim()
    if (couponCode) {
      couponStore.setCoupon({ code: couponCode })
    }

    return { code: 'product-added' }
  }

  const syncProductCoupon = (product: ProductModelV2) => {
    let removed = false

    coupons.value = coupons.value.filter((coupon) => {
      if (coupon.couponCode === product.coupon?.couponCode && !removed) {
        removed = true
        return false
      }
      return true
    })
  }

  const updateProductAmount = (product: ProductModelV2, amount = 1) => {
    if (amount > product.maxQuantity)
      amount = product.maxQuantity

    const productInCart = getProductInCart(product)
    if (productInCart)
      productInCart.amount = amount

    syncProductCoupon(product)
  }

  const removeProduct = async (product: ProductModelV2) => {
    const _products = cartData.value?.products.filter(p => p.pzn !== product.pzn)
    set(cartData, { ...cartData.value, products: _products })
    await checkoutStore.updateDraft()

    generalTracking?.removeFromCartEvent(product, (product.mainPrice?.value ?? 0) / 100)

    // set intial value to prevent error
    setCouponAlertMessage({ message: '', type: 'success' })
  }

  const addPrescription = (prescription: PrescriptionUniversal) => {
    if (prescription.filename) {
    // process scanned prescription
      set(cartData, {
        ...cartData.value,
        prescriptions: [...cartData.value.prescriptions, prescription as CheckoutPrescriptionModel],
      })
    } else {
    // process e-prescription
      set(cartData, {
        ...cartData.value,
        ePrescriptions: [...cartData.value.ePrescriptions, prescription as CheckoutEPrescriptionModel],
      })
    }
  }

  // TODO: test this
  const updatePrescription = (prescription: PrescriptionUniversal) => {
    if (prescription.filename) {
      const targetPrescription = cartData.value.prescriptions.find(p => p.filename === prescription.filename)
      if (targetPrescription)
        Object.assign(targetPrescription, prescription)
      else
        addPrescription(prescription)
    } else {
      const targetEPrescription = cartData.value.ePrescriptions.find(p => p.id === prescription.id)
      if (targetEPrescription)
        Object.assign(targetEPrescription, prescription)
      else
        addPrescription(prescription)
    }
  }

  const removePrescription = (prescription: PrescriptionUniversal) => {
    if (prescription.filename) {
    // process scanned prescription
      set(cartData, {
        ...cartData.value,
        prescriptions: cartData.value?.prescriptions.filter(p => p.filename !== prescription.filename),
      })
    } else {
    // process e-prescription
      set(cartData, {
        ...cartData.value,
        ePrescriptions: cartData.value?.ePrescriptions.filter(p => p.id !== prescription.id),
      })
    }
    generalTracking?.removeFromCartEvent(null, 0, prescription.filename ? PrescriptionVariant.REGULAR : PrescriptionVariant.ONLINE)
  }

  const updateMyLife = (myLife: boolean) => {
  // Prevent loop
    if (cartData.value.myLife === myLife)
      return

    cartData.value.myLife = myLife
  }

  const reset = () => {
    set(cartData, JSON.parse(JSON.stringify(DEFAULT_CART)))
    resetLegacyVuex()
    set(productsWithDetails, [])
  }

  return {
    isStoreInitialized,
    prescriptions,
    ePrescriptions,
    allPrescriptions,
    hasPrescriptions,
    hasPrescriptionsOnly,
    removePrescriptions,
    products,
    removeProducts,
    unavailableProducts,
    addUnavailableProduct,
    removeUnavailableProducts,
    hasNonPayableProducts,
    hasNonPayableProductsOnly,
    hasExpressProducts,
    hasOnlyExpressProducts,
    hasNonExpressProducts,
    hasCoupon,
    isLoading,
    productsWithDetails,
    cartQuantity,
    isCartEmpty,
    isCartMixed,
    isFetchingFirstProduct,
    cartData,
    addPrescription,
    removePrescription,
    updatePrescription,
    addProduct,
    updateProductAmount,
    removeProduct,
    removeCoupon,
    reset,
    updateMyLife,
    getProductInCart,
    getProductInCartAmount,
    fetchProductsWithDetails,
    coupons,
    validCoupons,
    couponAlert,
    setCouponAlertMessage,
  }
})
